import fontkit from "@pdf-lib/fontkit";
import {
  clip,
  endPath,
  PDFDocument,
  popGraphicsState,
  pushGraphicsState,
  rectangle,
  rgb,
  type PDFFont,
  type PDFImage,
  type PDFPage,
} from "pdf-lib";
import {
  ACTIVE_STATUSES,
  optLabel,
  PRIO,
  isVideoMime,
  PRIORITIES,
  STATUS_HEX,
  statusLabel,
  TASK_STATUS_LABELS,
  typeLabel,
  NLSFB,
  OBLIGATION_BASIS_APP_LABELS,
  OBLIGATION_KIND_LABELS,
  OBLIGATION_STATUS_LABELS,
  formatIntervalOf,
  obligationStatusLabel,
  nlsfbGroupCode,
  nlsfbLabel,
  nlsfbMainCode,
  type BuildingDto,
  type FloorDto,
  type IssueDetail,
  type LocationDto,
  type ObligationListRow,
  type ObligationStatus,
  type Priority,
  type TaskDto,
} from "@klantpunt/shared";
import { LOGO_BOX, type LogoPaths } from "./logo";

/**
 * PDF-rapport per gebouw in de huisstijl van de organisatie (SPEC §10), naar `buildReport()` in het
 * prototype. Alle tekst is echte tekst in het lettertype van die huisstijl (ingebed, subset); logo,
 * pins en ruimtes zijn vectoren.
 * Alleen de tekening en foto's zijn beelden. Geen DOM-afhankelijkheden: het laden van tekeningen
 * en foto's wordt aangereikt, zodat dit ook in Node (tests) draait.
 */

export type ReportScope = "actief" | "alle" | "filter";

export interface ReportOptions {
  title: string;
  author: string;
  scope: ReportScope;
  overview: boolean;
  maps: boolean;
  photos: boolean;
  log: boolean;
  /** Sectie *Onderhoud en keuringen* (module Assets). */
  inspections: boolean;
}

export interface PlanRaster {
  jpeg: Uint8Array;
  width: number;
  height: number;
}

/**
 * Huisstijl van het rapport. Het rapport gaat naar opdrachtgevers van de organisatie en draagt
 * daarom de huisstijl van die organisatie (instelling `report_brand`); standaard Klantpunt.
 */
export interface ReportStyle {
  key: "klantpunt" | "pandwachters";
  /** Naam in de PDF-eigenschappen en de standaardauteur. */
  name: string;
  accent: string;
  /** Achtergrond van het voorblad. */
  dark: string;
  /** Voettekst en voorblad (bv. pandwachters.nl). */
  site: string;
  slogan?: string;
  /** Teken voor kopjes: "+" (Pandwachters) of "•" (Klantpunt). */
  bullet: string;
  /** Logo voor het donkere voorblad en voor de kop van inhoudspagina's, met breedte in pt. */
  logoCover: LogoPaths;
  coverLogoW: number;
  logoPage: LogoPaths;
  pageLogoW: number;
  /** Klein "gemaakt met klantpunt.io" in de voettekst (rapporten in de stijl van een klant). */
  poweredBy: boolean;
  /** Eigen logo van de organisatie (PNG of JPEG); vervangt de logo's hierboven. */
  customLogo?: { bytes: Uint8Array; mime: "image/png" | "image/jpeg" };
  /** Eigen huisstijl van de organisatie: geen Klantpunt- of Pandwachters-beeldelementen op het voorblad. */
  custom?: boolean;
  /** Accent voor tekst op het donkere voorblad (wit als de accentkleur daar te weinig contrast heeft). */
  onDark?: string;
}

export interface ReportAssets {
  fonts: { bold: Uint8Array; regular: Uint8Array; light: Uint8Array };
  style: ReportStyle;
}

export interface ReportInput {
  building: BuildingDto;
  /** Al geselecteerd; volgorde wordt hier bepaald. */
  issues: IssueDetail[];
  tasks: TaskDto[];
  /** Verplichtingen bij assets in dit gebouw; alleen gebruikt met `options.inspections`. */
  obligations?: ObligationListRow[];
  options: ReportOptions;
  date: Date;
  assets: ReportAssets;
  loadPlan: (floor: FloorDto) => Promise<PlanRaster | null>;
  /** JPEG, al bijgesneden op 4:3. */
  loadPhoto: (fileId: string) => Promise<Uint8Array | null>;
  progress?: (message: string, fraction: number) => void;
}

export interface ReportResult {
  bytes: Uint8Array;
  pages: number;
  count: number;
}

// ---------- huisstijl ----------
export const PDFW = 595.28;
export const PDFH = 841.89;
export const PM = 42;
export const CW = PDFW - 2 * PM;
export const BOTTOM = PDFH - 56;
export const BRAND = {
  black: "#05080D",
  ink: "#0B1213",
  grey: "#5D6873",
  light: "#8C97A2",
  line: "#DDE1E5",
  soft: "#F4F5F6",
  white: "#FFFFFF",
  green: "#1F9A5C",
};
export const PRIOHEX: Record<Priority, string> = { P1: "#E3263F", P2: "#E09A12", P3: "#1F7FC4", P4: "#6B7884" };
/** Klantpunt-beeldmerk (punt + chevron) in eigen coördinaten (0–128 × 0–89). */
const KP_CHEVRON =
  "M85.66 87.32h42.15l-28.51-43.66 28.51-43.66H85.66c-2.72 0-5.25 1.37-6.73 3.64L52.79 43.66l26.14 40.02c1.48 2.27 4.02 3.64 6.73 3.64Z";
const KP_DOT = "M0 65.15a23.65 23.65 0 1 0 47.3 0a23.65 23.65 0 1 0 -47.3 0Z";
const PIN_PATH = "M15 37C15 37 2 23.5 2 14.5A13 13 0 0 1 28 14.5C28 23.5 15 37 15 37Z";

const color = (hex: string) => {
  const n = parseInt(hex.slice(1), 16);
  return rgb(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
};

/** Tekst zonder stuurtekens (die kan het font niet tekenen). */
const clean = (s: unknown) =>
  String(s ?? "")
    .replace(/[\t\r]/g, " ")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, "");

/** Woordomloop op echte fontmaten; te lange woorden worden afgebroken. */
export function wrapText(text: string, font: PDFFont, size: number, maxW: number): string[] {
  const tw = (s: string) => font.widthOfTextAtSize(s, size);
  const out: string[] = [];
  for (const par of clean(text).split("\n")) {
    const words = par.split(/\s+/).filter(Boolean);
    if (!words.length) {
      out.push("");
      continue;
    }
    let line = "";
    for (const w of words) {
      const cand = line ? `${line} ${w}` : w;
      if (tw(cand) <= maxW) {
        line = cand;
        continue;
      }
      if (line) out.push(line);
      if (tw(w) > maxW) {
        let part = "";
        for (const ch of w) {
          if (part && tw(part + ch) > maxW) {
            out.push(part);
            part = ch;
          } else part += ch;
        }
        line = part;
      } else line = w;
    }
    out.push(line);
  }
  return out;
}

/** Uitsnede rond de pin (prototype `snippetCanvas`): vierkant van 40 % van de breedte, binnen de tekening. */
export function snippetBox(W: number, H: number, pinX: number, pinY: number) {
  const side = Math.min(W * 0.4, W, H);
  const sx = Math.max(0, Math.min(W - side, pinX * W - side / 2));
  const sy = Math.max(0, Math.min(H - side, pinY * H - side * 0.55));
  return { sx, sy, side };
}

/** Volgorde zoals in het prototype: verdieping, prioriteit, volgnummer. */
export function sortForReport<T extends { locations: LocationDto[]; priority: string; seq: number }>(
  list: T[],
  floors: FloorDto[],
): T[] {
  const order = new Map([...floors].sort((a, b) => a.sort - b.sort).map((f, k) => [f.id, k]));
  const idx = (i: T) => order.get(primaryLoc(i)?.floorId ?? "") ?? 999;
  return [...list].sort((a, z) => idx(a) - idx(z) || a.priority.localeCompare(z.priority) || a.seq - z.seq);
}

export const primaryLoc = (i: { locations: LocationDto[] }): LocationDto | undefined =>
  i.locations.find((l) => l.isPrimary) ?? i.locations[0];

export const scopeLabel = (s: ReportScope) =>
  s === "actief" ? "Actieve bevindingen" : s === "filter" ? "Huidige filters in de app" : "Alle bevindingen";

export const reportFilename = (code: string, date: Date) => `Rapport ${code} ${date.toISOString().slice(0, 10)}.pdf`;

export const fmtDate = (iso: string) =>
  new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString("nl-NL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
const fmtDT = (iso: string) =>
  `${fmtDate(iso)} ${new Date(iso).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}`;
const plural = (n: number, one: string, more: string) => `${n} ${n === 1 ? one : more}`;

export type FontKey = "bold" | "regular" | "light";
interface TextOpts {
  f?: FontKey;
  color?: string;
  align?: "left" | "right" | "center";
}

/** Dunne laag over pdf-lib met coördinaten vanaf linksboven (zoals het prototype). */
export class Canvas {
  page!: PDFPage;
  /** Ingebed eigen logo (zie `ReportStyle.customLogo`). */
  customLogo?: PDFImage;
  constructor(
    readonly doc: PDFDocument,
    readonly fonts: Record<FontKey, PDFFont>,
  ) {}
  newPage() {
    this.page = this.doc.addPage([PDFW, PDFH]);
    return this.page;
  }
  width(s: string, size: number, f: FontKey = "regular") {
    return this.fonts[f].widthOfTextAtSize(clean(s), size);
  }
  rect(x: number, y: number, w: number, h: number, fill: string) {
    this.page.drawRectangle({ x, y: PDFH - y - h, width: w, height: h, color: color(fill) });
  }
  frame(x: number, y: number, w: number, h: number, stroke: string, lw = 0.6) {
    this.page.drawRectangle({
      x,
      y: PDFH - y - h,
      width: w,
      height: h,
      borderColor: color(stroke),
      borderWidth: lw,
    });
  }
  line(x1: number, y1: number, x2: number, y2: number, stroke: string, lw = 0.6) {
    this.page.drawLine({
      start: { x: x1, y: PDFH - y1 },
      end: { x: x2, y: PDFH - y2 },
      thickness: lw,
      color: color(stroke),
    });
  }
  poly(pts: [number, number][], fill: string) {
    this.svg(pts.map((p, k) => `${k ? "L" : "M"}${p[0]} ${p[1]}`).join("") + "Z", 0, 0, 1, { fill });
  }
  plus(x: number, y: number, s: number, fill: string) {
    const t = s * 0.28;
    this.rect(x - s / 2, y - t / 2, s, t, fill);
    this.rect(x - t / 2, y - s / 2, t, s, fill);
  }
  /** SVG-pad met de oorsprong op (x, y) linksboven. */
  svg(
    d: string,
    x: number,
    y: number,
    scale: number,
    o: { fill?: string; stroke?: string; lw?: number; opacity?: number },
  ) {
    this.page.drawSvgPath(d, {
      x,
      y: PDFH - y,
      scale,
      color: o.fill ? color(o.fill) : undefined,
      borderColor: o.stroke ? color(o.stroke) : undefined,
      borderWidth: o.stroke ? (o.lw ?? 1) : undefined,
      opacity: o.opacity,
    });
  }
  text(s: string, x: number, y: number, size: number, o: TextOpts = {}) {
    const f = o.f ?? "regular";
    const str = clean(s);
    const w = this.fonts[f].widthOfTextAtSize(str, size);
    const xx = o.align === "right" ? x - w : o.align === "center" ? x - w / 2 : x;
    if (str)
      this.page.drawText(str, { x: xx, y: PDFH - y, size, font: this.fonts[f], color: color(o.color ?? BRAND.ink) });
    return w;
  }
  wrap(s: string, size: number, maxW: number, f: FontKey = "regular") {
    return wrapText(s, this.fonts[f], size, maxW);
  }
  /** Alinea; geeft de nieuwe y na de laatste regel. */
  para(s: string, x: number, y: number, size: number, maxW: number, o: TextOpts & { lh?: number } = {}) {
    const lh = o.lh ?? size * 1.38;
    const lines = this.wrap(s, size, maxW, o.f);
    lines.forEach((l, k) => this.text(l, x, y + k * lh, size, o));
    return y + lines.length * lh;
  }
  image(img: PDFImage, x: number, y: number, w: number, h: number) {
    this.page.drawImage(img, { x, y: PDFH - y - h, width: w, height: h });
  }
  clipped(x: number, y: number, w: number, h: number, draw: () => void) {
    this.page.pushOperators(pushGraphicsState(), rectangle(x, PDFH - y - h, w, h), clip(), endPath());
    draw();
    this.page.pushOperators(popGraphicsState());
  }
  logo(paths: LogoPaths, word: string, accent: string, x: number, y: number, w: number) {
    const box = paths.box ?? LOGO_BOX;
    const s = w / box.w;
    const ox = x - box.x * s;
    const oy = y - box.y * s;
    const e = paths.emblem;
    if (e) {
      const ex = ox + e.tx * s;
      const ey = oy + e.ty * s;
      this.svg(e.shield, ex, ey, s * e.s, { fill: BRAND.white });
      this.svg(e.rim, ex, ey, s * e.s, { fill: word });
      this.svg(e.accent, ex, ey, s * e.s, { fill: accent });
    }
    if (paths.word) this.svg(paths.word, ox, oy, s, { fill: word });
    if (paths.accent) this.svg(paths.accent, ox, oy, s, { fill: accent });
  }
  /** Pin met de punt op (x, y); `s` = schaal van de 30×37-pin. */
  pin(x: number, y: number, s: number, fill: string, label: number | string) {
    this.svg(PIN_PATH, x - 15 * s, y - 37 * s, s, { fill, stroke: BRAND.white, lw: 2 });
    const t = String(label);
    const size = (t.length > 2 ? 9 : 11) * s;
    this.text(t, x, y - 37 * s + 14.5 * s + size * 0.36, size, { f: "bold", color: BRAND.white, align: "center" });
  }
}

/** Achtergrond van het voorblad in de huisstijl (Pandwachters: vlak met plusjes; Klantpunt: beeldmerk). */
export function coverArt(c: Canvas, st: ReportStyle) {
  const A = st.accent;
  c.rect(0, 0, PDFW, PDFH, st.dark);
  if (st.custom) {
    // Eigen huisstijl: rustig, alleen een accentbalk links.
    c.rect(0, 0, 10, PDFH, A);
  } else if (st.key === "pandwachters") {
    c.poly(
      [
        [0, 0],
        [PDFW, 0],
        [PDFW, 250],
        [PDFW / 2, 360],
        [0, 250],
      ],
      A,
    );
    for (let r = 0; r < 5; r++) for (let k = 0; k < 9; k++) c.plus(PDFW - PM - k * 15 - 4, 40 + r * 15, 6, st.dark);
  } else {
    // Klantpunt: groot beeldmerk (punt + chevron) rechtsboven.
    const ms = 1.6;
    c.svg(KP_CHEVRON, PDFW - PM - 128 * ms, 90, ms, { fill: A });
    c.svg(KP_DOT, PDFW - PM - 128 * ms, 90, ms, { fill: A, opacity: 0.85 });
  }
}

/** Embedden van de huisstijl-fonts en de PDF-eigenschappen. */
export async function brandedDoc(
  assets: ReportAssets,
  meta: { title: string; subject: string; author: string; date: Date },
) {
  const doc = await PDFDocument.create();
  doc.registerFontkit(fontkit);
  const fonts = {
    bold: await doc.embedFont(assets.fonts.bold, { subset: true }),
    regular: await doc.embedFont(assets.fonts.regular, { subset: true }),
    light: await doc.embedFont(assets.fonts.light, { subset: true }),
  };
  doc.setTitle(meta.title);
  doc.setAuthor(meta.author || assets.style.name);
  doc.setSubject(meta.subject);
  doc.setProducer("Klantpunt (klantpunt.io)");
  doc.setCreator("Klantpunt (klantpunt.io)");
  doc.setLanguage("nl-NL");
  doc.setCreationDate(meta.date);
  const c = new Canvas(doc, fonts);
  const own = assets.style.customLogo;
  if (own) {
    try {
      c.customLogo = own.mime === "image/png" ? await doc.embedPng(own.bytes) : await doc.embedJpg(own.bytes);
    } catch {
      c.customLogo = undefined; // onleesbaar logo: val terug op het standaardlogo
    }
  }
  return { doc, c };
}

/** Afmetingen van een afbeelding binnen een kader, met behoud van verhouding. */
const fit = (img: PDFImage, maxW: number, maxH: number) => {
  const k = Math.min(maxW / img.width, maxH / img.height);
  return { w: img.width * k, h: img.height * k };
};

/**
 * Logo op het donkere voorblad (bovenkant op y). Een eigen logo staat op een wit vlak, zodat ook een
 * donker logo leesbaar is.
 */
export function coverLogo(c: Canvas, st: ReportStyle, y = 420) {
  const img = c.customLogo;
  if (!img) return c.logo(st.logoCover, BRAND.white, st.accent, PM, y, st.coverLogoW);
  const { w, h } = fit(img, 220, 84);
  const pad = 14;
  c.rect(PM, y, w + 2 * pad, h + 2 * pad, BRAND.white);
  c.image(img, PM + pad, y + pad, w, h);
}

/** Kop van een inhoudspagina: logo, lijn en `kop` rechts. Geeft de y om te beginnen. */
export function contentPage(c: Canvas, st: ReportStyle, head: string) {
  c.newPage();
  let logoW = st.pageLogoW;
  if (c.customLogo) {
    const { w, h } = fit(c.customLogo, 110, 26);
    c.image(c.customLogo, PM, 38 - h / 2, w, h);
    logoW = w;
  } else c.logo(st.logoPage, BRAND.ink, st.accent, PM, 24, st.pageLogoW);
  // Lijn en teken schuiven mee met de breedte van de kop (minstens zoals bij een korte kop).
  const bx = PDFW - PM - Math.max(142, c.width(head, 8) + 10);
  c.line(PM + logoW + 12, 38, bx - 8, 38, st.accent, 0.8);
  c.text(st.bullet, bx, 41, 8, { f: "bold", color: st.accent });
  c.text(head, PDFW - PM, 41, 8, { color: BRAND.grey, align: "right" });
  return 76;
}

/** Sectielabel: huisstijlteken plus kapitalen. */
export function sectionLabel(c: Canvas, st: ReportStyle, s: string, x: number, y: number) {
  c.text(st.bullet, x, y, 8, { f: "bold", color: st.accent });
  c.text(s.toUpperCase(), x + 8, y, 7.5, { f: "bold", color: BRAND.grey });
}

/** Voetteksten op alle pagina's behalve het voorblad. */
export function footers(c: Canvas, st: ReportStyle, dstr: string) {
  const pages = c.doc.getPages();
  pages.forEach((p, k) => {
    if (k === 0) return;
    c.page = p;
    c.line(PM, PDFH - 40, PDFW - PM, PDFH - 40, BRAND.line, 0.6);
    c.text(`${st.site} · ${dstr}${st.poweredBy ? " · gemaakt met klantpunt.io" : ""}`, PM, PDFH - 27, 7.5, {
      color: BRAND.grey,
    });
    c.text(`Pagina ${k + 1} van ${pages.length}`, PDFW - PM, PDFH - 27, 7.5, { color: BRAND.grey, align: "right" });
  });
  return pages.length;
}

export const OBLIGATION_HEX: Record<ObligationStatus, string> = {
  overdue: "#E3263F",
  soon: "#E09A12",
  unknown: "#6B7884",
  ok: "#1F9A5C",
  na: "#8C97A2",
};
const OB_ORDER: ObligationStatus[] = ["overdue", "soon", "unknown", "ok"];
const intervalText = (m: number | null, w: number | null) =>
  w ? formatIntervalOf(null, w) : m == null ? "eenmalig" : m % 12 === 0 ? `${m / 12} jaar` : `${m} mnd`;

/**
 * Sectie *Onderhoud en keuringen*: telling per status en een tabel, slechtste eerst en dan op
 * vervaldatum. Niet van toepassing telt niet mee (alleen het aantal wordt genoemd).
 */
export function inspectionsSection(c: Canvas, st: ReportStyle, all: ObligationListRow[], startPage: () => number) {
  const list = all
    .filter((o) => o.status !== "na")
    .sort(
      (a, b) =>
        OB_ORDER.indexOf(a.status) - OB_ORDER.indexOf(b.status) ||
        // Daarna de volgorde van de server (aanmaakvolgorde); codes zijn willekeurig.
        (a.nextDueOn ?? "9999").localeCompare(b.nextDueOn ?? "9999"),
    );
  const na = all.length - list.length;
  let y = startPage();
  c.text("Onderhoud en keuringen", PM, y + 18, 22, { f: "bold" });
  y += 36;
  y =
    c.para(
      list.length
        ? `${plural(list.length, "keuring of onderhoudsbeurt", "onderhoud en keuringensbeurten")} bij de assets in dit gebouw` +
            (na ? `; ${na} niet van toepassing` : "") +
            ". Verlopen en binnenkort staan bovenaan."
        : "Er zijn nog geen onderhoud of keuringen vastgelegd bij de assets in dit gebouw.",
      PM,
      y,
      9.5,
      CW,
      { f: "light", color: BRAND.grey },
    ) + 8;
  if (!list.length) return;
  const w = (CW - 18) / 4;
  OB_ORDER.forEach((s, k) => {
    const x = PM + k * (w + 6);
    c.rect(x, y, w, 46, BRAND.soft);
    c.rect(x, y, 3, 46, OBLIGATION_HEX[s]);
    c.text(String(list.filter((o) => o.status === s).length), x + 12, y + 24, 18, { f: "bold" });
    c.text(OBLIGATION_STATUS_LABELS[s], x + 12, y + 38, 8, { f: "bold", color: BRAND.grey });
  });
  y += 62;
  const cols = [
    { l: "Asset", w: 118 },
    { l: "Keuring of onderhoud", w: 150 },
    { l: "Interval", w: 46 },
    { l: "Laatste", w: 62 },
    { l: "Volgende", w: 62 },
    { l: "Status", w: CW - 118 - 150 - 46 - 62 - 62 },
  ];
  const head = () => {
    c.rect(PM, y, CW, 18, BRAND.ink);
    let x = PM;
    for (const cl of cols) {
      c.text(cl.l.toUpperCase(), x + 6, y + 12, 7, { f: "bold", color: BRAND.white });
      x += cl.w;
    }
    y += 18;
  };
  head();
  list.forEach((o, k) => {
    const asset = c.wrap(o.asset ? `${o.asset.code} ${o.asset.name}` : "Bij het gebouw", 8, cols[0]!.w - 10);
    const name = c.wrap(o.name, 8.5, cols[1]!.w - 10);
    const basis = `${OBLIGATION_KIND_LABELS[o.kind]} · ${OBLIGATION_BASIS_APP_LABELS[o.basis]}`;
    const rh = 10 + Math.max(asset.length, name.length + 1) * 10.5;
    if (y + rh > BOTTOM) {
      y = startPage();
      head();
    }
    if (k % 2) c.rect(PM, y, CW, rh, BRAND.soft);
    let x = PM;
    asset.forEach((l, n) => c.text(l, x + 6, y + 13.5 + n * 10.5, 8, { f: n ? "regular" : "bold" }));
    x += cols[0]!.w;
    name.forEach((l, n) => c.text(l, x + 6, y + 13.5 + n * 10.5, 8.5));
    c.text(basis, x + 6, y + 13.5 + name.length * 10.5, 7.5, { color: BRAND.grey });
    x += cols[1]!.w;
    c.text(intervalText(o.intervalMonths, o.intervalWeeks), x + 6, y + 13.5, 8);
    x += cols[2]!.w;
    c.text(o.lastPerformedOn ? fmtDate(o.lastPerformedOn) : "—", x + 6, y + 13.5, 8);
    x += cols[3]!.w;
    c.text(o.nextDueOn ? fmtDate(o.nextDueOn) : "—", x + 6, y + 13.5, 8);
    x += cols[4]!.w;
    c.rect(x + 6, y + 6, 6, 6, OBLIGATION_HEX[o.status]);
    c.text(obligationStatusLabel(o.status, o.kind, o.lastRecord?.result), x + 16, y + 12.5, 8, { f: "bold" });
    y += rh;
    c.line(PM, y, PM + CW, y, BRAND.line, 0.5);
  });
}

export async function buildReport(input: ReportInput): Promise<ReportResult> {
  const { building: b, options: opts, assets } = input;
  const st = assets.style;
  const A = st.accent;
  const progress = input.progress ?? (() => {});
  const list = sortForReport(input.issues, b.floors);
  const taskById = new Map(input.tasks.map((t) => [t.id, t]));
  const floorById = new Map(b.floors.map((f) => [f.id, f]));
  const roomById = new Map(b.rooms.map((r) => [r.id, r]));
  const instById = new Map(b.assets.map((x) => [x.id, x]));
  const locLabel = (l: LocationDto) =>
    [floorById.get(l.floorId)?.name, l.roomId ? roomById.get(l.roomId)?.name : null].filter(Boolean).join(" · ") ||
    "Geen locatie";
  const locText = (i: IssueDetail) => {
    const main = primaryLoc(i);
    const parts = main ? [locLabel(main)] : [];
    const ins = i.installationId ? instById.get(i.installationId) : undefined;
    if (ins) parts.push(ins.name);
    const extra = Math.max(0, i.locations.length - 1);
    return (parts.join(" · ") || "Geen locatie") + (extra ? ` · +${extra} plek${extra > 1 ? "ken" : ""}` : "");
  };
  const dstr = input.date.toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });

  progress("Huisstijl voorbereiden…", 0);
  const { doc, c } = await brandedDoc(assets, {
    title: `${opts.title} ${b.name}`,
    subject: `${opts.title} · ${b.code}`,
    author: opts.author,
    date: input.date,
  });
  const counts = PRIORITIES.map((p) => list.filter((i) => i.priority === p && ACTIVE_STATUSES.has(i.status)).length);

  // ---------- voorblad ----------
  c.newPage();
  coverArt(c, st);
  coverLogo(c, st);
  const tw = c.text(opts.title, PM, 560, 34, { f: "bold", color: BRAND.white });
  c.text(".", PM + tw, 560, 34, { f: "bold", color: A });
  c.text(b.name, PM, 592, 18, { f: "bold", color: st.onDark ?? A });
  const meta: [string, string][] = [
    ["Adres", b.address || "—"],
    ["Opdrachtgever", (b.client.isUnassigned ? b.clientName : b.client.name) || "—"],
    ["Datum rapport", dstr],
    ["Opgesteld door", opts.author || "—"],
    ["Selectie", scopeLabel(opts.scope)],
  ];
  meta.forEach(([k, v], n) => {
    c.text(k.toUpperCase(), PM, 628 + n * 17, 7.5, { f: "bold", color: BRAND.light });
    c.text(v, PM + 92, 628 + n * 17, 10, { color: BRAND.white });
  });
  PRIORITIES.forEach((p, k) => {
    const x = PM + k * 128;
    const y = 730;
    c.rect(x, y, 4, 40, PRIOHEX[p]);
    c.text(String(counts[k]), x + 12, y + 22, 20, { f: "bold", color: BRAND.white });
    c.text(`${p} · ${PRIO[p].l}`.toUpperCase(), x + 12, y + 36, 7.5, { f: "bold", color: BRAND.light });
  });
  if (st.slogan) {
    const ow = c.text(st.slogan, PM, 808, 11, { color: BRAND.white });
    c.rect(PM + ow + 1.2, 805, 3, 3, A);
  }
  c.text(st.site, PDFW - PM, 808, 8.5, { f: "light", color: BRAND.light, align: "right" });

  // ---------- kop voor inhoudspagina's ----------
  const startPage = () => contentPage(c, st, `${opts.title} · ${b.code}`);
  const label = (s: string, x: number, y: number) => sectionLabel(c, st, s, x, y);

  // ---------- samenvatting ----------
  progress("Samenvatting opbouwen…", 0.02);
  let y = startPage();
  c.text("Samenvatting", PM, y + 18, 22, { f: "bold" });
  y += 36;
  const nOpen = list.filter((i) => ACTIVE_STATUSES.has(i.status)).length;
  const nDone = list.length - nOpen;
  y =
    c.para(
      `Dit rapport bevat ${plural(list.length, "bevinding", "bevindingen")} in ${b.name}${b.address ? `, ${b.address}` : ""}. ` +
        `${nOpen} ${nOpen === 1 ? "is" : "zijn"} nog actief${nDone ? `, ${nDone} ${nDone === 1 ? "is" : "zijn"} opgelost of gesloten` : ""}. ` +
        "De prioriteit volgt uit gevaar, impact en verergering; per bevinding staan locatie, foto's en het logboek vermeld.",
      PM,
      y,
      9.5,
      CW,
      { f: "light", color: BRAND.grey },
    ) + 8;
  PRIORITIES.forEach((p, k) => {
    const w = (CW - 18) / 4;
    const x = PM + k * (w + 6);
    c.rect(x, y, w, 60, BRAND.soft);
    c.rect(x, y, 3, 60, PRIOHEX[p]);
    c.text(String(counts[k]), x + 12, y + 25, 20, { f: "bold" });
    c.text(`${p} · ${PRIO[p].l}`, x + 12, y + 39, 8, { f: "bold", color: BRAND.grey });
    c.text(c.wrap(PRIO[p].t, 7, w - 20)[0] ?? "", x + 12, y + 51, 7, { color: BRAND.grey });
  });
  y += 78;
  // Telling per hoofdgroep en element (NLBE-SfB), alleen als er elementen zijn vastgelegd.
  const withEl = list.filter((i) => i.elementCode);
  if (withEl.length) {
    const tally = (key: (code: string) => string) => {
      const m = new Map<string, number>();
      for (const i of withEl) m.set(key(i.elementCode!), (m.get(key(i.elementCode!)) ?? 0) + 1);
      return [...m.entries()].sort((p, q) => q[1] - p[1] || p[0].localeCompare(q[0]));
    };
    const mains = tally(nlsfbMainCode).map(([m, n]) => `${m}- ${NLSFB.find((x) => x.code === m)?.label ?? ""} (${n})`);
    const top = tally(nlsfbGroupCode)[0]!;
    y =
      c.para(
        `Per hoofdgroep: ${mains.join(", ")}. Meeste bevindingen: ${nlsfbLabel(top[0])} (${top[1]}).`,
        PM,
        y,
        9,
        CW,
        { f: "regular", color: BRAND.grey },
      ) + 10;
  }
  const cols = [
    { k: "id", l: "ID", w: 62 },
    { k: "p", l: "Prio", w: 34 },
    { k: "t", l: "Bevinding", w: 176 },
    { k: "loc", l: "Locatie", w: 124 },
    { k: "st", l: "Status", w: 60 },
    { k: "tk", l: "Taak", w: CW - 62 - 34 - 176 - 124 - 60 },
  ] as const;
  const head = () => {
    c.rect(PM, y, CW, 18, BRAND.ink);
    let x = PM;
    for (const cl of cols) {
      c.text(cl.l.toUpperCase(), x + 6, y + 12, 7, { f: "bold", color: BRAND.white });
      x += cl.w;
    }
    y += 18;
  };
  head();
  list.forEach((i, k) => {
    const cells: Record<string, string[]> = {
      id: [i.code],
      t: c.wrap(i.title, 8.5, cols[2].w - 10),
      loc: c.wrap(locText(i), 8, cols[3].w - 10),
      st: [statusLabel(i.status)],
      tk: [i.taskCode ?? "—"],
    };
    const rh = 10 + Math.max(cells.t!.length, cells.loc!.length) * 10.5;
    if (y + rh > BOTTOM) {
      y = startPage();
      head();
    }
    if (k % 2) c.rect(PM, y, CW, rh, BRAND.soft);
    let x = PM;
    for (const cl of cols) {
      if (cl.k === "p") {
        c.rect(x + 5, y + 5, 24, 11, PRIOHEX[i.priority]);
        c.text(i.priority, x + 17, y + 13.2, 7, { f: "bold", color: BRAND.white, align: "center" });
      } else
        cells[cl.k]!.forEach((l, n) =>
          c.text(l, x + 6, y + 13.5 + n * 10.5, cl.k === "t" ? 8.5 : 8, {
            f: cl.k === "id" ? "bold" : "regular",
            color: cl.k === "loc" ? BRAND.grey : BRAND.ink,
          }),
        );
      x += cl.w;
    }
    y += rh;
    c.line(PM, y, PM + CW, y, BRAND.line, 0.5);
  });
  if (!list.length) c.text("Geen bevindingen in deze selectie.", PM, y + 16, 9.5, { color: BRAND.grey });

  // ---------- onderhoud en keuringen (module Assets, per rapport aan of uit) ----------
  if (opts.inspections && input.obligations?.length) {
    progress("Onderhoud en keuringen…", 0.04);
    inspectionsSection(c, st, input.obligations, startPage);
  }

  // ---------- tekeningen (één keer ingebed, vaker getekend) ----------
  const plans = new Map<string, Promise<{ img: PDFImage; W: number; H: number } | null>>();
  const getPlan = (f: FloorDto) => {
    let p = plans.get(f.id);
    if (!p) {
      p = (async () => {
        if (!f.plan) return null;
        try {
          const r = await input.loadPlan(f);
          return r ? { img: await doc.embedJpg(r.jpeg), W: r.width, H: r.height } : null;
        } catch {
          return null;
        }
      })();
      plans.set(f.id, p);
    }
    return p;
  };

  const floors = [...b.floors].sort((a, z) => a.sort - z.sort);
  if (opts.overview) {
    for (const f of floors) {
      const onF = list.filter((i) => i.locations.some((l) => l.floorId === f.id && l.x != null));
      if (!onF.length || !f.plan) continue;
      progress(`Overzicht ${f.name}…`, 0.05);
      const plan = await getPlan(f);
      if (!plan) continue;
      y = startPage();
      c.text(f.name, PM, y + 16, 20, { f: "bold" });
      c.text(`${plural(onF.length, "bevinding", "bevindingen")} op de tekening`, PDFW - PM, y + 16, 8.5, {
        color: BRAND.grey,
        align: "right",
      });
      y += 30;
      let w = CW;
      let h = (w * plan.H) / plan.W;
      if (h > 470) {
        h = 470;
        w = (h * plan.W) / plan.H;
      }
      const ox = PM + (CW - w) / 2;
      const oy = y;
      c.image(plan.img, ox, oy, w, h);
      c.frame(ox, oy, w, h, BRAND.line, 0.6);
      const s = Math.max(0.42, Math.min(0.6, w / 900));
      for (const i of onF)
        for (const l of i.locations)
          if (l.floorId === f.id && l.x != null && l.y != null)
            c.pin(ox + l.x * w, oy + l.y * h, s, PRIOHEX[i.priority], i.seq);
      y += h + 18;
      label("Legenda", PM, y);
      y += 12;
      const colW = (CW - 16) / 2;
      // Titel na de langste code (codes kunnen langer zijn dan W455-0007).
      const tx = 20 + Math.max(...onF.map((i) => c.width(i.code, 8.5, "bold"))) + 8;
      let col = 0;
      let ly = y;
      for (const i of onF) {
        const t = c.wrap(i.title, 8.5, colW - tx);
        const rh = Math.max(14, t.length * 10.5 + 4);
        if (ly + rh > BOTTOM) {
          if (col === 0) {
            col = 1;
            ly = y;
          } else {
            y = startPage();
            ly = y;
            col = 0;
          }
        }
        const x = PM + col * (colW + 16);
        c.rect(x, ly, 14, 14, PRIOHEX[i.priority]);
        c.text(String(i.seq), x + 7, ly + 10, 7.5, { f: "bold", color: BRAND.white, align: "center" });
        c.text(i.code, x + 20, ly + 10, 8.5, { f: "bold" });
        t.forEach((l, n) => c.text(l, x + tx, ly + 10 + n * 10.5, 8.5));
        ly += rh + 3;
      }
    }
  }

  // ---------- per bevinding ----------
  let n = 0;
  for (const i of list) {
    n++;
    progress(`Bevinding ${n} van ${list.length}: ${i.code}`, 0.1 + (0.85 * (n - 1)) / Math.max(1, list.length));
    y = startPage();
    const idw = c.text(i.code, PM, y + 18, 22, { f: "bold" });
    const chip = `${i.priority} · ${PRIO[i.priority].l}`;
    const cw = c.width(chip, 8, "bold") + 14;
    c.rect(PM + idw + 10, y + 4, cw, 17, PRIOHEX[i.priority]);
    c.text(chip, PM + idw + 17, y + 15.6, 8, { f: "bold", color: BRAND.white });
    c.text(statusLabel(i.status), PDFW - PM, y + 16, 10, {
      f: "bold",
      color: STATUS_HEX[i.status],
      align: "right",
    });
    y += 34;
    y = c.para(i.title, PM, y + 4, 15, CW, { f: "bold", lh: 19 });
    const main = primaryLoc(i);
    const f = main ? floorById.get(main.floorId) : undefined;
    const r = main?.roomId ? roomById.get(main.roomId) : undefined;
    const ins = i.installationId ? instById.get(i.installationId) : undefined;
    y = c.para([b.name, f?.name, r?.name, ins?.name].filter(Boolean).join("  ›  "), PM, y + 2, 9, CW, {
      color: BRAND.grey,
    });
    y += 4;
    c.line(PM, y, PDFW - PM, y, BRAND.line, 0.8);
    y += 16;

    const LW = 268;
    const RX = PM + LW + 20;
    const RW = CW - LW - 20;
    let ly = y;
    let ry = y;
    const pinned = i.locations.filter((l) => l.x != null && l.y != null);
    const others = (l0: LocationDto) => i.locations.filter((l) => l.id !== l0.id).map(locLabel);
    const l0 = pinned.find((l) => l.isPrimary) ?? pinned[0];
    const fl = l0 ? floorById.get(l0.floorId) : undefined;
    const plan = opts.maps && l0 && fl ? await getPlan(fl) : null;
    if (plan && l0) {
      const { sx, sy, side } = snippetBox(plan.W, plan.H, l0.x!, l0.y!);
      const k = RW / side;
      const top = ry;
      c.clipped(RX, top, RW, RW, () => {
        c.rect(RX, top, RW, RW, BRAND.white);
        c.image(plan.img, RX - sx * k, top - sy * k, plan.W * k, plan.H * k);
        const room = l0.roomId ? roomById.get(l0.roomId) : undefined;
        if (room?.shape) {
          const d =
            room.shape.points
              .map((p, q) => `${q ? "L" : "M"}${(p[0] * plan.W - sx) * k} ${(p[1] * plan.H - sy) * k}`)
              .join("") + "Z";
          c.svg(d, RX, top, 1, { fill: A, opacity: 0.16 });
          c.svg(d, RX, top, 1, { stroke: A, lw: 1.4 });
        }
        c.pin(RX + (l0.x! * plan.W - sx) * k, top + (l0.y! * plan.H - sy) * k, 0.8, PRIOHEX[i.priority], i.seq);
      });
      c.frame(RX, top, RW, RW, BRAND.line, 0.8);
      ry += RW + 11;
      ry = c.para(`Locatie op tekening: ${locLabel(l0)}`, RX, ry, 8, RW, { color: BRAND.grey });
      const rest = others(l0);
      if (rest.length) ry = c.para(`Ook aangetroffen: ${rest.join("; ")}`, RX, ry + 2, 8, RW, { color: BRAND.grey });
    } else if (i.locations.length > 1) {
      label("Plekken", RX, ry);
      ry += 12;
      ry = c.para(i.locations.map(locLabel).join("\n"), RX, ry, 9, RW);
    }
    if (i.description) {
      label("Toelichting", PM, ly);
      ly += 13;
      ly = c.para(i.description, PM, ly, 9.5, LW) + 8;
    }
    label("Beoordeling", PM, ly);
    ly += 13;
    const a = i.answers;
    const kv: [string, string][] = [
      ["Prioriteit", `${i.priority} ${PRIO[i.priority].l} – ${PRIO[i.priority].t}`],
      ["Type", typeLabel(a.type)],
      ...(i.elementCode ? ([["Element", nlsfbLabel(i.elementCode)]] as [string, string][]) : []),
      ["Gevaar", optLabel("danger", a.danger)],
      ["Impact", optLabel("impact", a.impact)],
      ["Verergert", optLabel("worsen", a.worsen)],
      ["Omvang", optLabel("size", a.size)],
      ["Oplosser", optLabel("solver", a.solver)],
      ["Herkomst", optLabel("origin", a.origin)],
      ["Vastgelegd", `${fmtDT(i.createdAt)} door ${i.createdBy?.name ?? "onbekend"}`],
    ];
    const t = i.taskId ? taskById.get(i.taskId) : undefined;
    if (t)
      kv.push([
        "Taak",
        `${t.code} · ${t.title}${t.dueDate ? ` (uiterlijk ${fmtDate(t.dueDate)})` : ""} · ${TASK_STATUS_LABELS[t.status]}` +
          (t.assigneeName ? ` · ${t.assigneeName}` : ""),
      ]);
    for (const [k, v] of kv) {
      c.text(k, PM, ly + 9, 8.5, { color: BRAND.grey });
      ly = Math.max(ly + 13, c.para(v, PM + 70, ly + 9, 9, LW - 70) - 9 + 4);
    }
    y = Math.max(ly, ry) + 14;

    // foto's
    // Video's passen niet in een PDF: alleen foto's, met een verwijzing naar de video's in de app.
    const photos = i.photos.filter((p) => !isVideoMime(p.mime));
    const videoCount = i.photos.length - photos.length;
    if (opts.photos && videoCount && !photos.length) {
      if (y + 30 > BOTTOM) y = startPage();
      c.text(`${videoCount === 1 ? "1 video" : `${videoCount} video's`} te bekijken in de app`, PM, y + 8, 8, {
        color: BRAND.grey,
      });
      y += 22;
    }
    if (opts.photos && photos.length) {
      if (y + 150 > BOTTOM) y = startPage();
      label(`Foto's (${photos.length})`, PM, y);
      y += 10;
      const gw = (CW - 2 * 10) / 3;
      const gh = gw * 0.75;
      let k = 0;
      for (const ph of photos.slice(0, 9)) {
        let bytes: Uint8Array | null = null;
        try {
          bytes = await input.loadPhoto(ph.fileId);
        } catch {
          bytes = null;
        }
        if (!bytes) continue;
        if (k % 3 === 0 && k > 0) y += gh + 10;
        if (y + gh > BOTTOM) y = startPage();
        const img = await doc.embedJpg(bytes);
        c.image(img, PM + (k % 3) * (gw + 10), y, gw, gh);
        k++;
      }
      if (k) y += gh + 8;
      const more = [
        photos.length > 9 ? `${photos.length - 9} foto's` : "",
        videoCount ? (videoCount === 1 ? "1 video" : `${videoCount} video's`) : "",
      ].filter(Boolean);
      if (more.length) {
        c.text(`+ ${more.join(" en ")} in de app`, PM, y + 8, 8, { color: BRAND.grey });
        y += 14;
      }
      y += 8;
    }

    // logboek
    if (opts.log && i.events.length) {
      const entries = [...i.events].sort((p, q) => (p.createdAt < q.createdAt ? 1 : -1)).slice(0, 12);
      if (y + 60 > BOTTOM) y = startPage();
      label("Logboek", PM, y);
      y += 8;
      for (const e of entries) {
        const lines = c.wrap(e.body ?? "", 9, CW - 110);
        const rh = lines.length * 12 + 8;
        if (y + rh > BOTTOM) y = startPage();
        c.line(PM, y, PDFW - PM, y, BRAND.line, 0.5);
        c.text(fmtDT(e.createdAt), PM, y + 13, 8, { color: BRAND.grey });
        c.text(e.user?.name ?? "Systeem", PM, y + 23, 7.5, { f: "bold", color: BRAND.light });
        lines.forEach((l, q) =>
          c.text(l, PM + 110, y + 13 + q * 12, 9, { color: e.kind === "comment" ? BRAND.ink : BRAND.grey }),
        );
        y += Math.max(rh, 28);
      }
    }
  }

  // ---------- voetteksten ----------
  const pageCount = footers(c, st, dstr);
  progress("PDF samenstellen…", 0.97);
  const bytes = await doc.save({ useObjectStreams: false });
  progress("Klaar", 1);
  return { bytes, pages: pageCount, count: list.length };
}
